import { CardContent } from '@mui/material'
import { styled } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import React from 'react'

import { SchemaPurchase } from '@/__generated/es-api'
import TablePagination from '@/app/component/atom/table-pagination'
import Loading from '@/app/component/guard/loading'
import TableRowActions from '@/app/component/layout/table/actions'
import { formatDate } from '@/app/service/util'

import DownloadReceiptDialog from './download-receipt-dialog'

type Props = {
  isFetching: boolean
  orgId: number
  receipts: SchemaPurchase[]
}

const Receipts: React.FC<Props> = ({ isFetching, orgId, receipts }) => {
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(15)
  const [toBeDownloaded, setToBeDownloaded] = React.useState<SchemaPurchase | undefined>()

  const handlePageChange = (newPage: number) => {
    setPage(newPage - 1)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
  }

  const downloadReceipt = (receipt: SchemaPurchase) => setToBeDownloaded(receipt)

  return (
    <Loading style={{ height: '100%' }} isLoading={isFetching}>
      <Container>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableHeadCell>Date</TableHeadCell>
                <TableHeadCell>Method</TableHeadCell>
                <TableHeadCell align="right">Amount</TableHeadCell>
                <TableHeadCell align="center">Actions</TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receipts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.created && formatDate(row.created)}</TableCell>
                  <TableCell>{row.transCode && paymentMethodMap[row.transCode]}</TableCell>
                  <TableCell align="right">{row.amount}</TableCell>
                  <TableCell align="center">
                    <ActionsContainer>
                      <TableRowActions
                        config={[
                          {
                            icon: 'download',
                            label: 'Download Receipt',
                            tooltip: 'Download this Receipt as PDF',
                            onClick: () => downloadReceipt(row),
                          },
                        ]}
                      />
                    </ActionsContainer>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          namespace="Receipts"
          page={page + 1}
          size={rowsPerPage}
          total={receipts.length}
          pageSizeValues={pageSizeValues}
          onPageSizeChange={handleRowsPerPageChange}
          onPageChange={handlePageChange}
        />
      </Container>
      {toBeDownloaded && (
        <DownloadReceiptDialog orgId={orgId} receipt={toBeDownloaded} onClose={() => setToBeDownloaded(undefined)} />
      )}
    </Loading>
  )
}

const Container = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
})

const TableContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  overflow: 'auto',

  '& table': {
    borderCollapse: 'separate',
    borderSpacing: 0,
    borderTop: 'none',
  },
})

const TableHeadCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

const ActionsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  '& > div': {
    marginLeft: 0,
    marginRight: 0,
  },
})

const pageSizeValues = [
  {
    label: '5',
    value: 5,
  },
  {
    label: '10',
    value: 10,
  },
  {
    label: '15',
    value: 15,
  },
  {
    label: '20',
    value: 20,
  },
  {
    label: '50',
    value: 50,
  },
  {
    label: '100',
    value: 100,
  },
]

const paymentMethodMap: Record<string, string> = {
  purchase_of_funds_via_credit_card: 'Credit Card',
  purchase_of_funds_via_paypal: 'PayPal',
  purchase_of_funds_via_bank_wire: 'Bank Wire',
}

export default Receipts
